import React, {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { SessionModalProvider } from "./helpers/SessionModalContext";
import SessionModalRoot from "./Components/SessionModalRoot";
import "./App.scss";
import { Route, Routes, useLocation, Navigate } from "react-router-dom";
import Header from "./Components/Header/Header";
import Footer from "./Components/Footer/Footer";
import { Alert, Box, Link } from "@mui/material";
import { CustomLoader } from "./helpers/CustomLoader";
import { authReducer, getInitialStoreState } from "./helpers/Authprovider";
import dayjs from "dayjs";
import axios from "axios";
import Reading from "./Components/Teacher/ClassroomReading/Reading";
import UploadWordMeaning from "./Pages/UploadWordMeaning/UploadWordMeaning";
import ScriptsDashboard from "./Pages/ScriptsDashboard/ScriptsDashboard";
import UploadUserSubscriptions from "./Pages/UploadUserSubscription/UploadUserSubscription";
import ScriptsRoutes from "./Pages/ScriptsDashboard/ScriptsRoutes";
import WordFilterPage from "./Pages/WordContent/WordContentPage";
import AdminDashboard from "./Pages/AdminDashboard/AdminDashboard";
import {
  DisplayScaleProvider,
  useDisplayScale,
} from "./helpers/DisplayScaleContext";

import FloatingZoomTip from "./Components/Common/FloatingZoomTip";
import DisplayScaleControl from "./Components/DisplayScaleControl";
import {
  debugEvent,
  debugEventOnce,
  debugNavigation,
  getAuthStorageSnapshot,
  getDeviceInfo,
  getStorageInfo,
  isAuthDebugEnabled,
} from "./helpers/AuthDebug";

const HomePage = React.lazy(() => import("./Pages/HomePage/HomePage"));
const ForSchool = React.lazy(() => import("./Pages/ForSchool/ForSchool"));
const Contact = React.lazy(() => import("./Pages/ContactUs/ContactUs"));
const AboutUs = React.lazy(() => import("./Pages/AboutUs/AboutUs"));
const Results = React.lazy(() => import("./Pages/Performance/Performance"));
const TeacherHome = React.lazy(() => import("./Pages/TeacherHome/TeacherHome"));
const StudentHome = React.lazy(() => import("./Pages/StudentHome/StudentHome"));
const Login = React.lazy(() => import("./Pages/Login/Login"));
const Phone = React.lazy(() => import("./Components/Phone/Phone"));
const TextbookHelp = React.lazy(
  () => import("./Pages/TextbookHelp/TextbookHelp"),
);
const ReadAloud = React.lazy(() => import("./Pages/ReadAloud/ReadAloud"));
const ClassroomActivity = React.lazy(
  () => import("./Pages/ClassroomActivity/ClassroomActivity"),
);
const DemoPage = React.lazy(() => import("./Pages/DemoPage/DemoPage"));
const DemoTest = React.lazy(() => import("./Pages/DemoTest/DemoTest"));
const Testformpage = React.lazy(
  () => import("./Pages/Testformpage/Testformpage"),
);
const Testformtextbookpage = React.lazy(
  () => import("./Pages/Testformtextbookpage/Testformtextbookpage"),
);
const Testformstorypage = React.lazy(
  () => import("./Pages/Testformstorypage/Testformstorypage"),
);
const PasageWordMapping = React.lazy(
  () => import("./Pages/PassageWordMapping/PassageWordMapping"),
);
const Testformdeletepage = React.lazy(
  () => import("./Pages/Testformdeletepage/Testformdeletepage"),
);
const Downloadreport = React.lazy(
  () => import("./Pages/Downloadreport/Downloadreport"),
);
const Testformimagepage = React.lazy(
  () => import("./Pages/Testformimagepage/Testformimagepage"),
);
const Testformdeleteimage = React.lazy(
  () => import("./Pages/Testformdeleteimage/Testformdeleteimage"),
);
const Testuseruploadform = React.lazy(
  () => import("./Pages/TestUserBulk/TestUserBulk"),
);
const Announcements = React.lazy(
  () => import("./Pages/Announcements/Announcements"),
);
const PrivacyPolicy = React.lazy(
  () => import("./Pages/PrivacyPolicy/PrivacyPolicy"),
);
const TermsAndConditions = React.lazy(
  () => import("./Pages/TermsAndConditions/TermsAndConditions"),
);
const TeacherDrawing = React.lazy(
  () => import("./Components/Drawing/teacherDrawing"),
);
const TestComp = React.lazy(() => import("./Components/test/test"));

export const AppStateContext = createContext(getInitialStoreState());
export const AppDispatchContext = createContext({});

export default function App() {
  const location = useLocation();
  const isTeacherHome = location.pathname.startsWith("/teacher-home");
  const isStudentHome = location.pathname.startsWith("/student-home");
  const isAdminPage =
    location.pathname.startsWith("/admin-dashboard") ||
    location.pathname.startsWith("/wordcontent") ||
    location.pathname.startsWith("/scripts");

  const [loading, setLoading] = useState(true);
  const [state, dispatch] = useReducer(authReducer, getInitialStoreState());
  const { scale } = useDisplayScale();

  const accessToken = localStorage.getItem("access_token");
  const expiryToken = localStorage.getItem("access_expiry");
  const user_type = localStorage.getItem("user_type");

  useEffect(() => {
    if (!isAuthDebugEnabled()) return;

    debugEvent("APP_AUTH_STATE_READ", {
      route: location.pathname,
      accessTokenPresent: !!accessToken,
      expiryPresent: !!expiryToken,
      userType: user_type || null,
      device: getDeviceInfo(),
      storage: getStorageInfo(),
      authStorage: getAuthStorageSnapshot(),
    });
  }, [location.pathname, accessToken, expiryToken, user_type]);

  useEffect(() => {
    if (accessToken && expiryToken) {
      const tokenExpiry = dayjs(expiryToken);
      const now = dayjs();

      if (tokenExpiry.isBefore(now)) {
        if (isAuthDebugEnabled()) {
          debugEvent("APP_TOKEN_EXPIRED", {
            route: location.pathname,
            deviceTime: now.toISOString(),
            accessExpiry: expiryToken,
            accessTokenPresent: !!accessToken,
            userType: user_type || null,
          });
        }
        dispatch({ type: "logout" });
      } else {
        if (isAuthDebugEnabled()) {
          debugEvent("APP_AUTH_REHYDRATED", {
            route: location.pathname,
            deviceTime: now.toISOString(),
            accessExpiry: expiryToken,
            accessTokenPresent: !!accessToken,
            userType: user_type || null,
          });
        }
        dispatch({ type: "access_token", access_token: accessToken });
        dispatch({ type: "user_type", user_type: user_type });
      }
    }
    setLoading(false);
  }, [accessToken, expiryToken, user_type]);

  if (loading) {
    return <CustomLoader />;
  }

  return (
    <SessionModalProvider>
      <AppDispatchContext.Provider value={dispatch}>
        <AppStateContext.Provider value={state}>
          <div
            className="praccel-app-scale-root"
            style={{
              zoom: scale,
              // Makes the scale readable inside calc() anywhere in this
              // subtree, so components can compensate vh/vw values that
              // would otherwise be double-scaled by the zoom above (see
              // ZoomPortal.jsx for the same on the portal root).
              "--praccel-display-scale": scale,
            }}
          >
            {!isTeacherHome && !isStudentHome && <Header />}

            <Suspense fallback={<CustomLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/school" element={<ForSchool />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/performance" element={<Results />} />
                <Route
                  path="/login"
                  element={
                    <RequireAuth redirect={"/"} noAuth>
                      <Login />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin-dashboard"
                  element={
                    <RequireAuth user_type="admin" redirect="/login">
                      <AdminDashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/teacher-home"
                  element={
                    <RequireAuth user_type="teacher" redirect="/login">
                      <TeacherHome />
                    </RequireAuth>
                  }
                >
                  <Route
                    path="session/:id"
                    element={
                      <RequireAuth user_type="teacher" redirect="/login">
                        <Reading />
                      </RequireAuth>
                    }
                  />
                </Route>
                <Route
                  path="/student-home"
                  element={
                    <RequireAuth user_type="student" redirect="/login">
                      <StudentHome />
                    </RequireAuth>
                  }
                />
                <Route path="/Phone" element={<Phone />} />
                <Route path="/textbook-help" element={<TextbookHelp />} />
                <Route path="/read-aloud" element={<ReadAloud />} />
                <Route path="/announcements" element={<Announcements />} />
                <Route path="/classroom-hub" element={<ClassroomActivity />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route
                  path="/terms-conditions"
                  element={<TermsAndConditions />}
                />
                <Route path="/demo" element={<DemoPage />} />
                <Route path="/demotest" element={<DemoTest />} />
                <Route path="/test" element={<TestComp />} />

                {/* All scripts routes are protected here */}
                <Route
                  path="/scripts/*"
                  element={
                    // <RequireAuth redirect="/login">
                    <ScriptsRoutes />
                    // </RequireAuth>
                  }
                />

                <Route path="/wordcontent" element={<WordFilterPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>

            {!isTeacherHome && !isStudentHome && <Footer />}
          </div>

          {/*
                  Persistent portal target for zoom-sensitive popups
                  (ImageLightbox, MeaningSection's POS popup,
                  GrammarPreviewPopup, and any future ZoomPortal
                  consumer). Deliberately a SIBLING of
                  .praccel-app-scale-root (not nested inside it) so its
                  own `zoom: scale` isn't compounded with the app
                  root's zoom. It has no transform/filter/perspective,
                  so `position: fixed` descendants portaled in here
                  still resolve against the real viewport.
                */}
          <div
            id="zoom-portal-root"
            style={{
              zoom: scale,
              "--praccel-display-scale": scale,
            }}
          />

          <DisplayScaleControl />
        </AppStateContext.Provider>
      </AppDispatchContext.Provider>
    </SessionModalProvider>
  );
}

function NotFound() {
  return (
    <Box
      sx={{
        display: "flex",
        margin: "128px auto",
        justifyContent: "center",
      }}
    >
      <Alert variant={"filled"} color={"warning"}>
        404 - Page Not Found <Link href={"/"}>click here</Link> for the home
        page
      </Alert>
    </Box>
  );
}

const RequireAuth = ({ noAuth, user_type, redirect, children }) => {
  const { access_token, ...rest } = useContext(AppStateContext);
  const saved_user_type = rest.user_type;

  if (noAuth) {
    const allowed = !access_token;

    if (isAuthDebugEnabled()) {
      debugEventOnce(
        `guard:${window.location.pathname}:noAuth:${!!access_token}:${allowed}`,
        "AUTH_GUARD_CHECK",
        {
          route: window.location.pathname,
          noAuth: true,
          accessTokenPresent: !!access_token,
          savedUserType: saved_user_type || null,
          requiredUserType: null,
          result: allowed ? "PASS" : "FAIL",
          redirect: allowed ? null : redirect,
        }
      );
    }

    if (allowed) {
      return children;
    }

    if (isAuthDebugEnabled()) {
      debugNavigation(redirect);
    }

    return <Navigate replace to={redirect} />;
  }

  const allowed = !!access_token && saved_user_type === user_type;

  if (isAuthDebugEnabled()) {
    debugEventOnce(
      `guard:${window.location.pathname}:protected:${!!access_token}:${saved_user_type || ""}:${user_type || ""}:${allowed}`,
      "AUTH_GUARD_CHECK",
      {
        route: window.location.pathname,
        noAuth: false,
        accessTokenPresent: !!access_token,
        savedUserType: saved_user_type || null,
        requiredUserType: user_type || null,
        result: allowed ? "PASS" : "FAIL",
        redirect: allowed ? null : (redirect || "/"),
      }
    );
  }

  if (allowed) {
    return children;
  }

  if (isAuthDebugEnabled()) {
    debugNavigation(redirect || "/");
  }

  return <Navigate replace to={redirect || "/"} />;
};
